import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import BigButton from '../components/BigButton'
import Field from '../components/Field'
import { api } from '../lib/api'
import { eventDateSentence, reopenSentence, useSeason } from '../lib/season'
import type { Payment } from '../lib/types'

const PRICE_PER_PLACE_EUR = 5

function formatEur(cents: number): string {
  const whole = Math.trunc(cents / 100)
  const rest = Math.abs(cents % 100).toString().padStart(2, '0')
  return `${whole},${rest}`
}

type Errors = Partial<Record<'naam' | 'email' | 'gewenstAdres' | 'aantalPlaatsen', string>>

export default function Inschrijven() {
  const nav = useNavigate()
  const [naam, setNaam] = useState('')
  const [email, setEmail] = useState('')
  const [gewenstAdres, setGewenstAdres] = useState('')
  const [buurtbewoner, setBuurtbewoner] = useState(false)
  const [aantalPlaatsen, setAantal] = useState(1)
  const [opmerkingen, setOpmerkingen] = useState('')
  const [website, setWebsite] = useState('') // honeypot
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<Errors>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const { season, waitlist, loading: seasonLoading } = useSeason()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setServerError(null)
    setErrors({})
    const result = await api<{
      registration: { token: string; naam: string }
      emailSent: boolean
      buurtbewoner: boolean
      wachtlijst: boolean
      assigned: string[]
      payment: Payment
    }>(
      '/api/register',
      {
        method: 'POST',
        body: JSON.stringify({ naam, email, gewenstAdres, aantalPlaatsen, opmerkingen, website, buurtbewoner }),
      },
    )
    setBusy(false)
    if (!result.ok) {
      if (result.error === 'gesloten') {
        setServerError('De inschrijvingen zijn intussen gesloten. Herlaad de pagina.')
      } else if (result.errors) setErrors(result.errors as Errors)
      else setServerError('Er ging iets mis. Probeer het zo opnieuw.')
      return
    }
    // Skip the "check your email" step — drop the user straight on their
    // own page. The confirmation email is still sent and works as a
    // recovery link, but it's no longer a required hop in the flow.
    nav(`/mijn-plek/${encodeURIComponent(result.data.registration.token)}`)
  }

  const subtotaalCents = aantalPlaatsen * PRICE_PER_PLACE_EUR * 100
  // Buurtbewoners zijn vrijgesteld van de wachtlijst (zie routes/register.ts).
  const opWachtlijst = waitlist && !buurtbewoner

  // Wacht op de seizoensstatus voor we iets tonen — anders staat het volledige
  // (indienbare) formulier hier even open by default terwijl /api/season nog
  // loopt, en kan iemand op een gesloten seizoen toch nog invullen/versturen
  // vlak voor het "gesloten"-scherm alsnog verschijnt.
  if (seasonLoading) {
    return <div className="container-narrow py-12 text-muted">Laden…</div>
  }

  // Seizoen gesloten: de editie is voorbij. Geen formulier, wel duidelijk
  // wanneer het weer kan — en waar mensen met een bestaande link terechtkunnen.
  if (season && !season.registrationOpen) {
    return (
      <div className="container-narrow py-10">
        <h1 className="font-display text-6xl tracking-display uppercase mb-1">Inschrijven</h1>
        <div className="card p-6 shadow-block mt-6">
          <p className="font-display text-3xl tracking-display uppercase mb-2">
            De inschrijvingen zijn gesloten
          </p>
          <p className="text-lg">
            De rommelmarkt{season.editionYear ? ` van ${season.editionYear}` : ''} is voorbij —
            bedankt aan iedereen die erbij was! Inschrijven voor de volgende editie kan opnieuw{' '}
            <strong>{reopenSentence(season)}</strong>.
          </p>
          <p className="text-base text-muted mt-3">
            Heb je vorige editie meegedaan? Je persoonlijke link uit je mail blijft werken — daar kan je
            je inschrijving nog bekijken en je feedback achterlaten.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Link to="/praktisch"><BigButton>Praktische info</BigButton></Link>
            <a
              href="mailto:rommelmarkt@fluweelfeest.be"
              className="text-sm text-muted underline self-center"
            >
              rommelmarkt@fluweelfeest.be
            </a>
          </div>
        </div>
      </div>
    )
  }

  // Voor wélke markt schrijf je je in? Het jaartal komt van de server
  // (activeEditionYear() schuift vanzelf op zodra het seizoen heropent), de
  // datum uit /beheer. Zonder datum blijft enkel het jaartal staan — nooit
  // de datum van vorig jaar.
  const editieDatum = eventDateSentence(season)
  return (
    <div className="container-narrow py-10">
      <p className="font-display text-xs sm:text-sm tracking-display uppercase text-muted">
        Rommelmarkt {season?.activeEdition ?? ''}{editieDatum ? ` · ${editieDatum}` : ''}
      </p>
      <h1 className="font-display text-6xl tracking-display uppercase mb-1">Inschrijven</h1>
      <p className="text-muted mb-6">Vul deze gegevens in. Eén minuutje werk.</p>

      {waitlist && (
        <div className="mb-6 border-[3px] border-ink bg-lime shadow-block p-4">
          <p className="font-display text-2xl tracking-display uppercase mb-1">De rommelmarkt is volzet</p>
          <p className="text-base">
            Je kan je nog inschrijven, maar dan kom je op de <strong>wachtlijst</strong> — we kunnen je nog
            geen plek beloven. Komt er een plek vrij, dan contacteren we je. Je hoeft nu nog niets te betalen.
          </p>
          <p className="text-base mt-2">
            <strong>Woon je zelf in de straat?</strong> Dan geldt de wachtlijst niet voor jou: vink hieronder
            "Ik woon in deze straat" aan en we reserveren gewoon de plek voor jouw gevel.
          </p>
        </div>
      )}

      <form onSubmit={submit} className="card p-6 shadow-block">
        <Field label="Je naam" htmlFor="naam" required help="Voor- en achternaam." error={errors.naam}>
          <input
            id="naam"
            className="input"
            value={naam}
            onChange={(e) => setNaam(e.target.value)}
            autoComplete="name"
          />
        </Field>

        <Field
          label="Je e-mailadres"
          htmlFor="email"
          required
          help="Hierop sturen we je je bevestiging en de link om je plek te kiezen."
          error={errors.email}
        >
          <input
            id="email"
            className="input"
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </Field>

        <div className="mb-4 mt-2 border-[3px] border-ink p-4 bg-lime/30">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 size-5 accent-ink"
              checked={buurtbewoner}
              onChange={(e) => setBuurtbewoner(e.target.checked)}
            />
            <span className="text-base">
              <strong>Ik woon in deze straat</strong> (Fluweelstraat / Kempstraat / Tortelduifstraat).
              Dan reserveren we automatisch de plek voor jouw gevel.
            </span>
          </label>
        </div>

        <Field
          label={buurtbewoner ? 'Adres van je gevel' : 'Gewenst adres'}
          htmlFor="gewenstAdres"
          required
          help={
            buurtbewoner
              ? 'We reserveren automatisch de plek voor jouw gevel. Schrijf je in vóór 31 augustus om zeker te zijn van voorrang.'
              : 'Geef je voorkeurstraat door (bv. Fluweelstraat). Wij wijzen je een plek toe en laten het je per mail weten.'
          }
          error={errors.gewenstAdres}
        >
          <input
            id="gewenstAdres"
            className="input"
            value={gewenstAdres}
            onChange={(e) => setGewenstAdres(e.target.value)}
            autoComplete="street-address"
          />
        </Field>

        <Field
          label="Aantal plaatsen"
          htmlFor="aantalPlaatsen"
          required
          help="Eén plaats is ongeveer 5 lopende meter en kost €5. Meestal volstaat 1."
          error={errors.aantalPlaatsen}
        >
          <select
            id="aantalPlaatsen"
            className="input"
            value={aantalPlaatsen}
            onChange={(e) => setAantal(Number(e.target.value))}
          >
            {[1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <div className="mt-2 inline-flex items-center gap-2 border-[2px] border-ink bg-lime px-3 py-1.5 text-sm font-display tracking-display uppercase">
            <span className="text-ink/60">Subtotaal</span>
            <strong className="text-ink">€{formatEur(subtotaalCents)}</strong>
          </div>
        </Field>

        <Field
          label="Opmerkingen"
          htmlFor="opmerkingen"
          help="Optioneel. Bv. een wens over de plek, of als je naast iemand wil staan."
        >
          <textarea
            id="opmerkingen"
            className="input min-h-[6rem]"
            value={opmerkingen}
            onChange={(e) => setOpmerkingen(e.target.value)}
            rows={3}
          />
        </Field>

        {/* Honeypot — should remain empty. */}
        <div className="hidden" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            id="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>

        {serverError && (
          <div className="mb-4 border-[3px] border-red bg-red/10 px-4 py-3 text-sm text-red font-semibold">
            {serverError}
          </div>
        )}

        <p className="text-sm text-muted mb-3">
          {opWachtlijst
            ? 'Je komt op de wachtlijst. Je hoeft nu nog niets te betalen — dat vragen we pas als er een plek voor je vrijkomt.'
            : <>Na inschrijven krijg je een gestructureerde mededeling om <strong>€{formatEur(subtotaalCents)}</strong> over te schrijven (of scan de QR-code met je bank-app). Je plek staat vast zodra de betaling binnen is.</>}
        </p>

        <BigButton type="submit" disabled={busy} className="w-full">
          {busy ? 'Bezig met versturen…' : opWachtlijst ? 'Op de wachtlijst zetten' : 'Inschrijven'}
        </BigButton>
      </form>
    </div>
  )
}
